import React, { useEffect, useRef, useState } from 'react';
import { useAtom } from 'jotai';
import { collapseAtom } from '../../utils/atoms/collapseAtom';
import { sideMenu } from '../../utils/atoms/sideMenuAtom';

/**
 * ChartWidthAdjuster
 * Reusable helper component that listens to side menu open/close state and performs a
 * width-only adjustment on an existing chart WITHOUT triggering a full redraw.
 * Shows a loader (via parent state handler) for a fixed delay (default 500ms) then
 * invokes the provided adjustWidth callback to resize scales / paths in place.
 *
 * Props:
 *  - chartRef: React ref to the root chart container (its opacity is toggled)
 *  - adjustWidth: function executed after the delay to perform width-only update
 *  - delay (ms): delay before calling adjustWidth (default 500)
 *  - enabled: boolean guard (e.g., only after initial draw & visible)
 *  - setLoading: parent setter for loading state (optional). If omitted, internal state used.
 */
const ChartWidthAdjuster = ({ chartRef, adjustWidth, delay = 500, enabled = true, setLoading, preAdjust, freezeWidth = false, remeasureOnRelease = true }) => {
  const [collapseSideMenu] = useAtom(collapseAtom);
  const [sideMenuShow] = useAtom(sideMenu);
  const timerRef = useRef(null);
  const [internalLoading, setInternalLoading] = useState(false);
  const setBusy = setLoading || setInternalLoading;

  useEffect(() => {
    if (!enabled) return; // do nothing if not ready
    // Trigger only when side menu states change
    const rootEl = chartRef?.current;
    let originalWidthStyle = null;
    if (rootEl) {
      if (freezeWidth) {
        // Freeze current width to prevent layout shift during side menu animation
        originalWidthStyle = rootEl.style.width;
        const currentWidth = rootEl.getBoundingClientRect().width;
        rootEl.style.width = currentWidth + 'px';
      }
      rootEl.style.opacity = '0';
    }
  // Optional immediate pre-adjust (e.g., shrink oversized SVG right away to avoid horizontal push)
  try { if (preAdjust) preAdjust(); } catch(_) {}
    setBusy(true);
    if (timerRef.current) clearTimeout(timerRef.current);
    timerRef.current = setTimeout(() => {
      try { adjustWidth && adjustWidth(); } catch(_) {}
      if (rootEl) {
        rootEl.style.opacity = '1';
        if (freezeWidth) {
          // Release width lock back to responsive after initial adjustment
          requestAnimationFrame(() => {
            rootEl.style.width = originalWidthStyle || '100%';
            if (remeasureOnRelease) {
              // Allow layout to settle then re-run width adjust if width actually changed
              requestAnimationFrame(() => {
                try { adjustWidth && adjustWidth(); } catch(_) {}
              });
            }
          });
        }
      }
      setBusy(false);
    }, delay);
    return () => {
      if (timerRef.current) clearTimeout(timerRef.current);
    };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [collapseSideMenu?.rightMenu, sideMenuShow, enabled, adjustWidth, delay, preAdjust, freezeWidth, remeasureOnRelease]);

  return null; // purely behavioral
};

export default ChartWidthAdjuster;
